import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { RiPrinterLine } from '@remixicon/react';
import SAVOIRS from '../../data/savoirs';
import SavoirArticle from './SavoirSections';
import FicheIcon from './ficheIcons';
import { setFicheRead } from '../../data/ficheProgress';
import './FicheWindow.css';

/* Une fiche se LIT dans une fenêtre (décision 2026-09-16) : le panneau
   Savoirs liste et guide, mais une fiche longue, avec ses tableaux et sa
   figure, demande la largeur d'une page. Fermeture par ✕, Échap ou le fond ;
   un lien « Dans Palestre » navigue derrière et referme.
   Arrivé au bout de la fiche, on l'a LUE (2026-10-09) : un repère invisible
   ferme le texte, et la fiche passe dans « Lues » quand il paraît. */
export default function FicheWindow({ ficheId, onClose, setPage, activeTenant, year }) {
  const { t } = useTranslation();
  const fiche = ficheId ? SAVOIRS.find((f) => f.id === ficheId) : null;
  const endRef = useRef(null);
  useEffect(() => {
    const el = endRef.current;
    if (!fiche || !el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setFicheRead(fiche.id, true); io.disconnect(); }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [fiche]);
  useEffect(() => {
    if (!fiche) return undefined;
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [fiche, onClose]);
  if (!fiche) return null;
  return createPortal(
    <div className="fw-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="fw" role="dialog" aria-modal="true" aria-label={fiche.title}>
        <header className="fw-head">
          <FicheIcon fiche={fiche} size={17} />
          <span className="fw-cat">{fiche.category}</span>
          {fiche.updated && <span className="fw-date">· {t('common:knowledge.updated')} {fiche.updated}</span>}
          <button type="button" className="fw-btn" onClick={() => window.print()}
                  title={t('common:actions.print')} aria-label={t('common:actions.print')}>
            <RiPrinterLine size={16} aria-hidden="true" />
          </button>
          <button type="button" className="fw-close" onClick={onClose}
                  title={t('common:actions.close')} aria-label={t('common:actions.close')}>
            <X size={16} strokeWidth={1.8} aria-hidden="true" />
          </button>
        </header>
        <div className="fw-body">
          <SavoirArticle fiche={fiche} tenantId={activeTenant?.id} year={year}
                         onNavigate={(key) => { onClose(); setPage && setPage(key); }} />
          <div ref={endRef} style={{ height: 1 }} aria-hidden="true" />
          <p className="fw-print-foot">www.palestre.ch · www.myfinancepartner.ch</p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
